/**
 * Módulo de validadores reutilizables
 * Sigue principios SOLID para mantener código DRY y mantenible
 */

/**
 * Valida el formato de un dominio (ej: midominio.com)
 * @param {string} domain - El dominio a validar
 * @param {boolean} required - Si el campo es requerido
 * @returns {Object} { valid: boolean, error: string|null }
 */
export const validateDomain = (domain, required = false) => {
  if (!domain && !required) {
    return { valid: true, error: null };
  }
  if (!domain && required) {
    return { valid: false, error: 'El dominio es requerido' };
  }
  // No espacios, no arroba, no caracteres especiales, solo letras, números, guiones y puntos
  const domainPattern = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.[A-Za-z0-9-]{1,63}(?<!-))*\.[A-Za-z]{2,}$/;
  // Debe tener al menos un punto y TLD de mínimo 2 letras
  if (!domain.includes('.') || !domainPattern.test(domain)) {
    return { valid: false, error: 'El dominio debe tener un formato válido (ej: midominio.com)' };
  }
  return { valid: true, error: null };
};

/**
 * Valida el formato y dominio de un email
 * Equivalente a la validación 'email:rfc,dns' de Laravel (sin verificación DNS real por limitaciones del navegador)
 * 
 * @param {string} email - El email a validar
 * @param {boolean} required - Si el campo es requerido
 * @returns {Object} { valid: boolean, error: string|null }
 */
export const validateEmail = (email, required = false) => {
  // Si no es requerido y está vacío, es válido
  if (!email && !required) {
    return { valid: true, error: null };
  }

  // Si es requerido y está vacío
  if (!email && required) {
    return { valid: false, error: 'El email es requerido' };
  }

  // Validación RFC del formato de email
  const emailPattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!emailPattern.test(email)) {
    return { valid: false, error: 'El formato del email no es válido' };
  }

  // Validación del dominio
  const domain = email.split('@')[1];
  
  // Verificar que el dominio tenga al menos un punto
  if (!domain || !domain.includes('.')) {
    return { valid: false, error: 'El dominio del email debe incluir un TLD válido (ej: .com, .mx)' };
  }

  // Verificar que el TLD tenga al menos 2 caracteres
  const tld = domain.split('.').pop();
  if (tld.length < 2) {
    return { valid: false, error: 'El dominio debe tener una extensión válida (mínimo 2 caracteres)' };
  }

  // Verificar que no termine o empiece con punto o guión
  if (domain.startsWith('.') || domain.endsWith('.') || 
      domain.startsWith('-') || domain.endsWith('-')) {
    return { valid: false, error: 'El dominio tiene un formato inválido' };
  }

  return { valid: true, error: null };
};

/**
 * Valida un teléfono (10 dígitos)
 * 
 * @param {string} phone - El teléfono a validar
 * @param {boolean} required - Si el campo es requerido
 * @returns {Object} { valid: boolean, error: string|null }
 */
export const validatePhone = (phone, required = false) => {
  if (!phone && !required) {
    return { valid: true, error: null };
  }

  if (!phone && required) {
    return { valid: false, error: 'El teléfono es requerido' };
  }

  const phonePattern = /^\d{10}$/;
  if (!phonePattern.test(phone.replace(/\s|-/g, ''))) {
    return { valid: false, error: 'El teléfono debe tener 10 dígitos' };
  }

  return { valid: true, error: null };
};

/**
 * Valida un código postal mexicano (5 dígitos)
 * 
 * @param {string} postalCode - El código postal a validar
 * @param {boolean} required - Si el campo es requerido
 * @returns {Object} { valid: boolean, error: string|null }
 */
export const validatePostalCode = (postalCode, required = false) => {
  if (!postalCode && !required) {
    return { valid: true, error: null };
  }

  if (!postalCode && required) {
    return { valid: false, error: 'El código postal es requerido' };
  }

  const postalPattern = /^\d{5}$/;
  if (!postalPattern.test(postalCode)) {
    return { valid: false, error: 'El código postal debe tener 5 dígitos' };
  }

  return { valid: true, error: null };
};

/**
 * Valida que un campo no esté vacío
 * 
 * @param {any} value - El valor a validar
 * @param {string} fieldName - Nombre del campo para el mensaje de error
 * @returns {Object} { valid: boolean, error: string|null }
 */
export const validateRequired = (value, fieldName = 'Este campo') => {
  const isEmpty = value === null || value === undefined || 
                  (typeof value === 'string' && value.trim() === '');
  
  return {
    valid: !isEmpty,
    error: isEmpty ? `${fieldName} es requerido` : null
  };
};

/**
 * Valida múltiples campos y retorna todos los errores
 * 
 * @param {Object} validations - Objeto con las validaciones a ejecutar
 * @returns {Object} { isValid: boolean, errors: Object }
 * 
 * @example
 * const result = validateMultiple({
 *   email: validateEmail(form.email, true),
 *   phone: validatePhone(form.phone),
 *   nombre: validateRequired(form.nombre, 'El nombre')
 * });
 * 
 * if (!result.isValid) {
 *   this.errors = result.errors;
 * }
 */
export const validateMultiple = (validations) => {
  const errors = {};
  let isValid = true;

  Object.keys(validations).forEach(field => {
    const validation = validations[field];
    if (!validation.valid) {
      errors[field] = validation.error;
      isValid = false;
    }
  });

  return { isValid, errors };
};
